import { NavLink, Outlet } from 'react-router';
import { VerifiedBadge } from '../components/Badges';
import { ButtonLink } from '../components/Button';
import { Icon, type IconName } from '../components/Icon';
import { RoadrunnerMark } from '../components/illustrations';
import { useMe } from '../features/auth/session';
import { BoardAside } from '../features/feed/BoardAside';
import styles from './AppShell.module.css';

const NAV: { to: string; label: string; icon: IconName }[] = [
  { to: '/', label: 'Home', icon: 'home' },
  { to: '/notices', label: 'Notices', icon: 'board' },
  { to: '/me', label: 'Me', icon: 'person' },
];

export function AppShell() {
  const { data: me } = useMe();
  const isMod = me?.role === 'moderator' || me?.role === 'admin';
  const place = me?.location?.placeName;

  return (
    <div className={styles.shell}>
      <a href="#main" className="skip-link">
        Skip to main content
      </a>

      <header className={styles.topbar}>
        <NavLink to="/" className={styles.topbarBrand}>
          <RoadrunnerMark size={30} />
          Roadrunner
        </NavLink>
        {place && (
          <span className={styles.topbarPlace}>
            {place}
            <VerifiedBadge label="Verified" />
          </span>
        )}
      </header>

      <nav className={styles.sidenav} aria-label="Main">
        <NavLink to="/" className={styles.brand}>
          <RoadrunnerMark size={44} />
          <span className={styles.wordmark}>Roadrunner</span>
        </NavLink>
        {place && (
          <div className={styles.place}>
            <span>{place}</span>
            <VerifiedBadge />
          </div>
        )}
        <ul className={styles.navList}>
          {NAV.map((item) => (
            <li key={item.to}>
              <NavLink to={item.to} end={item.to === '/'} className={styles.navLink}>
                <Icon name={item.icon} />
                {item.label}
              </NavLink>
            </li>
          ))}
          {isMod && (
            <li>
              <NavLink to="/mod" className={styles.navLink}>
                <Icon name="shield" />
                Moderation
              </NavLink>
            </li>
          )}
          {me?.role === 'admin' && (
            <li>
              <NavLink to="/admin/geo" className={styles.navLink}>
                <Icon name="map" />
                Geo debug
              </NavLink>
            </li>
          )}
        </ul>
        <ButtonLink to="/post/new" variant="primary" block>
          <Icon name="pencil" size={18} />
          Post a note
        </ButtonLink>
        <p className={styles.navFoot}>
          A neighborhood board for the northern Tucson suburbs.
          <span className={styles.navFootLinks}>
            <NavLink to="/about">About</NavLink>
            <NavLink to="/privacy">Privacy</NavLink>
            <NavLink to="/guidelines">Guidelines</NavLink>
            <NavLink to="/terms">Terms</NavLink>
          </span>
        </p>
      </nav>

      <main id="main" className={styles.main} tabIndex={-1}>
        <Outlet />
      </main>

      <aside className={styles.aside} aria-label="About your board">
        {me?.location && <BoardAside />}
      </aside>

      <nav className={styles.tabbar} aria-label="Main">
        <NavLink to="/" end className={styles.tab}>
          <Icon name="home" size={22} />
          Home
        </NavLink>
        <NavLink to="/notices" className={styles.tab}>
          <Icon name="board" size={22} />
          Notices
        </NavLink>
        <NavLink to="/post/new" className={styles.tab} aria-label="Post a note">
          <span className={styles.tabPost}>
            <Icon name="pencil" size={20} />
          </span>
        </NavLink>
        <NavLink to="/me" className={styles.tab}>
          <Icon name="person" size={22} />
          Me
        </NavLink>
      </nav>
    </div>
  );
}
